/**
 * Entity Cache Service
 * 
 * Provides persistent IndexedDB caching for entity metadata.
 * 
 * NOTE: The Zustand subscription that previously wrote entities to this cache
 * has been removed — entity caching now flows through the entity-system Y.Doc's
 * y-indexeddb layer (managed by entitySystemYjsStore). This service is retained
 * for:
 *   - computeEntitiesHash() — used by staleness detection in entityStore
 *   - get() / delete() / clear() — used during logout and dataspace switching
 *   - Future per-entity caching if needed
 */

import { BaseEntityType } from '../types';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('ENTITY_CACHE');

const DB_NAME = 'river_entity_cache';
const DB_VERSION = 1;
const STORE_NAME = 'entities';
const CURRENT_CACHE_VERSION = '1.0.1';

interface CacheEntry {
  userId: string;
  entities: BaseEntityType[];
  timestamp: number;
  version: string;
  entitiesHash: string;
  entityTimestamps: Record<string, string>;
}

function idbRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

class EntityCacheService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  private async init(): Promise<void> {
    if (this.db) return;
    
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        log.error('[EntityCache] Failed to open IndexedDB:', request.error);
        this.initPromise = null;
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;

        this.db.onclose = () => {
          log.warn('[EntityCache] IDB connection closed unexpectedly');
          this.db = null;
        };

        this.db.onversionchange = () => {
          log.warn('[EntityCache] IDB version changed in another tab, closing');
          this.db?.close();
          this.db = null;
        };

        log.info('[EntityCache] IndexedDB initialized successfully');
        this.initPromise = null;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
          log.info('[EntityCache] Created object store');
        }
      };
    });

    return this.initPromise;
  }

  private async ensureDb(): Promise<IDBDatabase | null> {
    await this.init();
    return this.db;
  }

  /**
   * Compute a deterministic hash from entity timestamps for staleness detection.
   * Pure utility — no IndexedDB access.
   */
  computeEntitiesHash(timestamps: Record<string, string>): string {
    const sortedEntries = Object.entries(timestamps)
      .sort(([a], [b]) => a.localeCompare(b));
    
    const hashInput = sortedEntries
      .map(([llmId, updatedAt]) => `${llmId}:${updatedAt}`)
      .join('|');
    
    return this.simpleStringHash(hashInput);
  }

  /** 32-bit FNV-1a */
  private simpleStringHash(str: string): string {
    let hash = 2166136261;
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return (hash >>> 0).toString(36);
  }

  async get(userId: string): Promise<CacheEntry | null> {
    try {
      const db = await this.ensureDb();
      if (!db) {
        log.warn('[EntityCache] Database not initialized');
        return null;
      }

      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const entry = await idbRequest<CacheEntry | undefined>(store.get(userId));

      if (!entry) {
        return null;
      }

      if (entry.version !== CURRENT_CACHE_VERSION) {
        log.info('[EntityCache] Cache version mismatch, invalidating', {
          cached: entry.version,
          current: CURRENT_CACHE_VERSION
        });
        await this.delete(userId);
        return null;
      }

      const age = Date.now() - entry.timestamp;
      log.info('[EntityCache] Cache hit', {
        entityCount: entry.entities.length,
        ageMinutes: (age / (60 * 1000)).toFixed(1),
        hash: entry.entitiesHash?.substring(0, 8) || 'none'
      });
      
      return entry;
    } catch (error) {
      log.error('[EntityCache] Error reading cache:', error);
      return null;
    }
  }

  async set(userId: string, entities: BaseEntityType[]): Promise<void> {
    try {
      const db = await this.ensureDb();
      if (!db) {
        log.warn('[EntityCache] Database not initialized, skipping cache write');
        return;
      }

      const hasDocContent = entities.some(e => 
        e.entityType === 'Doc' && 
        (e.skeleton as any)?.base64 && 
        (e.skeleton as any).base64.length > 100
      );
      
      if (hasDocContent) {
        log.error('[EntityCache] SAFETY VIOLATION: Attempted to cache document content!');
        return;
      }

      const entityTimestamps: Record<string, string> = {};
      const flatEntities = this.flattenEntities(entities);
      for (const entity of flatEntities) {
        if (entity.llmId && entity.updated_at) {
          entityTimestamps[entity.llmId] = entity.updated_at;
        }
      }
      
      const entitiesHash = this.computeEntitiesHash(entityTimestamps);

      const entry: CacheEntry = {
        userId,
        entities,
        timestamp: Date.now(),
        version: CURRENT_CACHE_VERSION,
        entitiesHash,
        entityTimestamps,
      };

      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      await idbRequest(store.put(entry));

      log.info('[EntityCache] Cache updated successfully', {
        entityCount: entities.length,
        hash: entitiesHash.substring(0, 8)
      });
    } catch (error) {
      log.error('[EntityCache] Error writing cache:', error);
    }
  }

  private flattenEntities(entities: BaseEntityType[]): BaseEntityType[] {
    const result: BaseEntityType[] = [];
    const traverse = (entity: BaseEntityType) => {
      result.push(entity);
      if (entity.children && Array.isArray(entity.children)) {
        entity.children.forEach(traverse);
      }
    };
    entities.forEach(traverse);
    return result;
  }

  async delete(userId: string): Promise<void> {
    try {
      const db = await this.ensureDb();
      if (!db) return;

      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      await idbRequest(store.delete(userId));

      log.info('[EntityCache] Cache cleared for user');
    } catch (error) {
      log.error('[EntityCache] Error deleting cache:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      const db = await this.ensureDb();
      if (!db) return;

      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      await idbRequest(store.clear());

      log.info('[EntityCache] All cache cleared');
    } catch (error) {
      log.error('[EntityCache] Error clearing cache:', error);
    }
  }

  async has(userId: string): Promise<boolean> {
    const cacheEntry = await this.get(userId);
    return cacheEntry !== null;
  }

  async getStats(): Promise<{ count: number; totalSize: number }> {
    try {
      const db = await this.ensureDb();
      if (!db) return { count: 0, totalSize: 0 };

      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const count = await idbRequest(store.count());

      return { count, totalSize: 0 };
    } catch (error) {
      log.error('[EntityCache] Error getting stats:', error);
      return { count: 0, totalSize: 0 };
    }
  }

  async getAllCacheEntries(): Promise<CacheEntry[]> {
    try {
      const db = await this.ensureDb();
      if (!db) return [];

      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      return await idbRequest<CacheEntry[]>(store.getAll());
    } catch (error) {
      log.error('[EntityCache] Error getting all entries:', error);
      return [];
    }
  }
}

export const entityCacheService = new EntityCacheService();
